import { BarChart3, Route, Store, type LucideIcon } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';

type UseCase = {
  num: string;
  Icon: LucideIcon;
  title: string;
  desc: string;
  platform: string;
};

const USE_CASES: ReadonlyArray<UseCase> = [
  {
    num: '01',
    Icon: Store,
    title: 'Encaissement comptoir',
    desc: "L'app Desktop tourne sur un PC bas de gamme, imprime les tickets sur ESC/POS, marche sans réseau. Le stock se met à jour quand la connexion revient.",
    platform: 'Desktop',
  },
  {
    num: '02',
    Icon: Route,
    title: 'Vente terrain · livraison',
    desc: "L'app Android dans la poche du commercial ou du livreur. Scan code-barres, paiement Mobile Money, reçu WhatsApp. Pas besoin de tablette dédiée.",
    platform: 'Mobile',
  },
  {
    num: '03',
    Icon: BarChart3,
    title: 'Pilotage à distance',
    desc: "Le gérant ou le comptable se connecte depuis n'importe quel navigateur. Tableau de bord, rapports, gestion équipe — même s'il est en voyage.",
    platform: 'Web',
  },
];

export default function DownloadUseCasesV2() {
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>3 usages · 3 plateformes</SectionLabel>
        <h2 className="mt-3 font-marketing-display text-[clamp(32px,4.4vw,56px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
          Le bon outil, <span className="underline-lime">au bon endroit.</span>
        </h2>
        <p className="mt-4 max-w-xl font-marketing-body text-[17px] text-charcoal-600">
          Chaque plateforme cible un usage opérationnel précis. Installe celles qui te servent, les
          données se synchronisent automatiquement.
        </p>

        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {USE_CASES.map(({ num, Icon, title, desc, platform }) => (
            <article
              key={num}
              className="flex h-full flex-col gap-5 border-2 border-charcoal-800 bg-paper p-7 shadow-neobrutal-6 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-10"
            >
              <div className="flex items-start justify-between">
                <span className="font-marketing-display text-[40px] font-bold leading-none tracking-[-0.04em] text-lime-500">
                  {num}
                </span>
                <span className="border-[1.5px] border-charcoal-800 bg-white px-2 py-1 font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-charcoal-800">
                  {platform}
                </span>
              </div>

              <span className="flex h-11 w-11 items-center justify-center border-2 border-charcoal-800 bg-white text-charcoal-800 shadow-neobrutal-2">
                <Icon className="h-[22px] w-[22px]" strokeWidth={2} />
              </span>

              <h3 className="font-marketing-display text-[19px] font-bold leading-[1.2] tracking-[-0.01em] text-charcoal-800">
                {title}
              </h3>
              <p className="flex-1 font-marketing-body text-[14px] leading-[1.6] text-charcoal-600">
                {desc}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
